import {
  IHolidayFilterOptionData,
  IHolidayFilterState,
  IHolidaySearchQueryParams,
  IHolidaySearchResult,
} from "../types/holiday-search-type";
import { getHolidayDurationBucket } from "./extract-holiday-filter-data";

function parsePrice(value: string | number | null | undefined): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const normalized = Number(value.replaceAll(",", ""));
    if (Number.isFinite(normalized)) {
      return normalized;
    }
  }

  return null;
}

function unique(values?: string[]) {
  return Array.from(new Set((values ?? []).map((item) => item.trim()))).filter(
    Boolean,
  );
}

export function buildHolidayFilterState(
  searchParams: IHolidaySearchQueryParams,
  optionData: IHolidayFilterOptionData,
): IHolidayFilterState {
  const minPrice = searchParams.min_price ?? optionData.minPrice;
  const maxPrice = searchParams.max_price ?? optionData.maxPrice;

  return {
    query: searchParams.query ?? "",
    minPrice: Number.isFinite(minPrice) ? minPrice : optionData.minPrice,
    maxPrice: Number.isFinite(maxPrice) ? maxPrice : optionData.maxPrice,
    holidayTypes: unique(searchParams.type),
    holidayFor: [],
    durations: unique(searchParams.duration),
  };
}

export function clampHolidayPriceRange(
  state: IHolidayFilterState,
  optionData: IHolidayFilterOptionData,
): IHolidayFilterState {
  const nextMin = Math.max(state.minPrice ?? optionData.minPrice, optionData.minPrice);
  const nextMax = Math.min(state.maxPrice ?? optionData.maxPrice, optionData.maxPrice);

  return {
    ...state,
    minPrice: Math.min(nextMin, nextMax),
    maxPrice: Math.max(nextMin, nextMax),
  };
}

export function filterHolidayResult(
  result: IHolidaySearchResult[] | undefined,
  filters: IHolidayFilterState,
): IHolidaySearchResult[] {
  if (!result?.length) {
    return [];
  }

  const query = filters.query.trim().toLowerCase();

  return result.filter((holiday) => {
    const price = parsePrice(holiday.price);
    const durationBucket = getHolidayDurationBucket(Number(holiday.duration));
    const title = holiday.title?.toLowerCase() ?? "";
    const cities = (holiday.cities ?? [])
      .map((item) => item.city_name.toLowerCase())
      .join(" ");

    if (query && !title.includes(query) && !cities.includes(query)) {
      return false;
    }

    if (
      typeof filters.minPrice === "number" &&
      price !== null &&
      price < filters.minPrice
    ) {
      return false;
    }

    if (
      typeof filters.maxPrice === "number" &&
      price !== null &&
      price > filters.maxPrice
    ) {
      return false;
    }

    if (
      filters.holidayTypes.length > 0 &&
      !filters.holidayTypes.includes(holiday.holiday_type)
    ) {
      return false;
    }

    if (
      filters.durations.length > 0 &&
      !filters.durations.includes(durationBucket)
    ) {
      return false;
    }

    return true;
  });
}
